/**
 * Catalog Importer — task C.0 — first real ingest path.
 *
 * Accepts CSV / JSON-LD / vendor-CSV strings, validates every row against
 * the ontology fields declared in codegen.json, and materializes typed
 * SKU nodes with provenance (wasGeneratedBy → CatalogImport → fromSource).
 *
 * Per-project localStorage today; KV/Pages-Function hardening lands in the
 * next iter (8a-cont). Renderer chain (`photoForSkuNode`, `Scene3D`,
 * `CatalogRail`) is unchanged — the contract is the typed SKU node.
 */
import { z } from "zod";
import codegen from "../../../docs/ontology/codegen.json";
import type { GraphNode, GraphEdge } from "../types/ontology";

// ─── Source enum + payload shapes ──────────────────────────────────────

export const CatalogSourceSchema = z.enum([
  "kaggle-csv",       // Kaggle CSV — kg-5 IKEA-SA, kg-6 e-com 2024
  "vendor-csv",       // Studio's own CSV
  "schema.org",       // Retailer JSON-LD
  "foyr-neo",         // Chrome-extension pull from neo.foyr.com
]);
export type CatalogSource = z.infer<typeof CatalogSourceSchema>;

// Allowed SKU category enum — pulled directly from codegen so the field
// stays the single source of truth.
const CATEGORY_ENUM: ReadonlyArray<string> = (() => {
  const fields = (codegen.fields ?? []) as ReadonlyArray<{ path: string; enum?: ReadonlyArray<string> }>;
  const cat = fields.find((f) => f.path === "SKU.category");
  return cat?.enum ?? [];
})();

// Schema for a single imported row, AFTER mapping. All ontology-typed.
export const ImportedSkuSchema = z.object({
  label: z.string().min(1),
  category: z.string().refine((v) => CATEGORY_ENUM.length === 0 || CATEGORY_ENUM.includes(v),
    { message: `category must be one of: ${CATEGORY_ENUM.join(", ")}` }),
  price: z.number().nonnegative().optional(),
  leadDays: z.number().int().nonnegative().optional(),
  styleMatch: z.number().min(0).max(1).optional(),
  vendor: z.string().optional(),
  photoUrl: z.string().url().optional(),
  modelUrl: z.string().url().optional(),
  gtin: z.string().optional(),
});
export type ImportedSku = z.infer<typeof ImportedSkuSchema>;

export interface IngestResult {
  readonly source: CatalogSource;
  readonly datasetId: string;
  readonly importedAt: string;
  readonly skus: ReadonlyArray<GraphNode>;
  readonly skipped: ReadonlyArray<{ readonly rowIndex: number; readonly reason: string }>;
  /** Typed prov:Activity node — ONE per ingest run. Carries source +
   *  datasetId + atTime (xsd:dateTime) + rowCount. Each emitted SKU
   *  carries a WAS_GENERATED_BY edge pointing at this node so the
   *  Why-engine can answer "where did this product come from?". */
  readonly importActivity: GraphNode;
  /** SKU → CatalogImport provenance edges, one per emitted SKU. */
  readonly provenanceEdges: ReadonlyArray<GraphEdge>;
}

/** Build the typed :CatalogImport node + the WAS_GENERATED_BY edges
 *  from emitted SKUs.  Used by both ingestCsv and ingestSeedEntries
 *  so the provenance shape stays consistent across paths. */
function buildImportActivity(
  source: CatalogSource,
  datasetId: string,
  projectId: string,
  nowIso: string,
  skuIds: ReadonlyArray<string>,
  skippedCount: number,
): { node: GraphNode; edges: GraphEdge[] } {
  const id = `import-${projectId}-${datasetId}-${nowIso.replace(/[:.]/g, "-")}`;
  const node: GraphNode = {
    id,
    kind: "CatalogImport",
    label: `Imported ${skuIds.length} SKU${skuIds.length === 1 ? "" : "s"} from ${datasetId}`,
    attrs: {
      atTime: nowIso,
      source,
      datasetId,
      rowCount: skuIds.length,
      skippedRows: skippedCount,
    },
  };
  const edges: GraphEdge[] = skuIds.map((sid) => ({
    id: `edge-wgb-${sid}-${id}`,
    kind: "WAS_GENERATED_BY",
    source: sid,
    target: id,
    weight: 1,
    attrs: { atTime: nowIso },
  }));
  return { node, edges };
}

// ─── CSV parser (RFC 4180-lite — handles quoted commas, escaped quotes) ─

export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === "\"" && text[i + 1] === "\"") { cell += "\""; i += 1; continue; }
      if (ch === "\"") { inQuotes = false; continue; }
      cell += ch;
      continue;
    }
    if (ch === "\"") { inQuotes = true; continue; }
    if (ch === ",") { row.push(cell); cell = ""; continue; }
    if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i += 1;
      row.push(cell); cell = "";
      if (row.length > 0 && row.some((c) => c.length > 0)) rows.push(row);
      row = [];
      continue;
    }
    cell += ch;
  }
  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    if (row.some((c) => c.length > 0)) rows.push(row);
  }
  return rows;
}

// ─── Column mapper ─────────────────────────────────────────────────────

export interface ColumnMap {
  readonly label: string;          // required source col
  readonly category: string;       // required source col (must map to enum)
  readonly price?: string;
  readonly leadDays?: string;
  readonly styleMatch?: string;
  readonly vendor?: string;
  readonly photoUrl?: string;
  readonly modelUrl?: string;
  readonly gtin?: string;
}

function pickByHeader(row: string[], headers: string[], colName: string | undefined): string | undefined {
  if (!colName) return undefined;
  const ix = headers.indexOf(colName);
  if (ix < 0) return undefined;
  const v = row[ix];
  return v && v.length > 0 ? v : undefined;
}

function parseNumberLoose(s: string | undefined): number | undefined {
  if (s === undefined) return undefined;
  // strip currency glyphs + commas + spaces
  const cleaned = s.replace(/[^\d.\-]/g, "");
  if (cleaned.length === 0) return undefined;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : undefined;
}

// ─── Ingest entry point ────────────────────────────────────────────────

interface IngestArgs {
  readonly source: CatalogSource;
  readonly datasetId: string;          // e.g. "kg-5-ikea-sa", or a user-supplied tag
  readonly csv: string;                // raw CSV text
  readonly columnMap: ColumnMap;
  readonly projectId: string;
  readonly nowIso?: string;
}

export function ingestCsv(args: IngestArgs): IngestResult {
  const rows = parseCsv(args.csv);
  if (rows.length < 2) {
    const nowEmpty = args.nowIso ?? new Date().toISOString();
    const { node: emptyNode, edges: emptyEdges } = buildImportActivity(args.source, args.datasetId, args.projectId, nowEmpty, [], 1);
    return {
      source: args.source,
      datasetId: args.datasetId,
      importedAt: nowEmpty,
      skus: [],
      skipped: [{ rowIndex: -1, reason: "empty or header-only CSV" }],
      importActivity: emptyNode,
      provenanceEdges: emptyEdges,
    };
  }
  const headers = rows[0] ?? [];
  const dataRows = rows.slice(1);
  const skus: GraphNode[] = [];
  const skipped: { rowIndex: number; reason: string }[] = [];
  const now = args.nowIso ?? new Date().toISOString();

  for (let i = 0; i < dataRows.length; i += 1) {
    const r = dataRows[i] ?? [];
    const rawRow: Record<string, unknown> = {
      label:      pickByHeader(r, headers, args.columnMap.label),
      category:   pickByHeader(r, headers, args.columnMap.category),
      price:      parseNumberLoose(pickByHeader(r, headers, args.columnMap.price)),
      leadDays:   parseNumberLoose(pickByHeader(r, headers, args.columnMap.leadDays)),
      styleMatch: parseNumberLoose(pickByHeader(r, headers, args.columnMap.styleMatch)),
      vendor:     pickByHeader(r, headers, args.columnMap.vendor),
      photoUrl:   pickByHeader(r, headers, args.columnMap.photoUrl),
      modelUrl:   pickByHeader(r, headers, args.columnMap.modelUrl),
      gtin:       pickByHeader(r, headers, args.columnMap.gtin),
    };
    const parsed = ImportedSkuSchema.safeParse(rawRow);
    if (!parsed.success) {
      skipped.push({ rowIndex: i + 1, reason: parsed.error.issues.map((iss) => `${iss.path.join(".")}: ${iss.message}`).join(" · ") });
      continue;
    }
    const sku = parsed.data;
    const id = `imp-${args.datasetId}-${i + 1}-${(args.projectId).slice(0, 8)}`;
    const attrs: Record<string, string | number | boolean | null> = {
      category: sku.category,
      // Provenance per CLAUDE.md §12 — every imported row traceable.
      _source: args.source,
      _datasetId: args.datasetId,
      _rowIndex: i + 1,
      _importedAt: now,
    };
    if (sku.price !== undefined)       attrs["price"]       = sku.price;
    if (sku.leadDays !== undefined)    attrs["leadDays"]    = sku.leadDays;
    if (sku.styleMatch !== undefined)  attrs["styleMatch"]  = sku.styleMatch;
    if (sku.vendor !== undefined)      attrs["vendor"]      = sku.vendor;
    if (sku.photoUrl !== undefined)    attrs["photoUrl"]    = sku.photoUrl;
    if (sku.modelUrl !== undefined)    attrs["modelUrl"]    = sku.modelUrl;
    if (sku.gtin !== undefined)        attrs["gtin"]        = sku.gtin;
    skus.push({ id, kind: "SKU", label: sku.label, attrs });
  }

  const { node: importActivity, edges: provenanceEdges } = buildImportActivity(
    args.source, args.datasetId, args.projectId, now, skus.map((s) => s.id), skipped.length,
  );
  return {
    source: args.source,
    datasetId: args.datasetId,
    importedAt: now,
    skus,
    skipped,
    importActivity,
    provenanceEdges,
  };
}

// ─── Persistence (localStorage today; KV hardening next) ───────────────

const KEY_PREFIX = "foyrmind.catalogImports.";

export function readImportedCatalog(projectId: string): ReadonlyArray<GraphNode> {
  try {
    const raw = window.localStorage.getItem(KEY_PREFIX + projectId);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((n): n is GraphNode =>
      Boolean(n) && typeof n === "object"
      && (n as GraphNode).kind === "SKU"
      && typeof (n as GraphNode).id === "string",
    );
  } catch (err) {
    // CLAUDE.md §5 — surface, never silently swallow.
    // eslint-disable-next-line no-console
    console.error("[catalogIngest] failed to read", err);
    return [];
  }
}

export function appendImportedCatalog(projectId: string, skus: ReadonlyArray<GraphNode>): void {
  const existing = readImportedCatalog(projectId);
  const existingIds = new Set(existing.map((s) => s.id));
  const merged = [...existing, ...skus.filter((s) => !existingIds.has(s.id))];
  try {
    window.localStorage.setItem(KEY_PREFIX + projectId, JSON.stringify(merged));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[catalogIngest] failed to write", err);
  }
}

export function clearImportedCatalog(projectId: string): void {
  try { window.localStorage.removeItem(KEY_PREFIX + projectId); } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[catalogIngest] failed to clear", err);
  }
}

// ─── Import-activity persistence (typed :CatalogImport nodes + edges) ──

const ACTIVITY_KEY_PREFIX = "foyrmind.importActivities.";

interface PersistedActivity {
  readonly node: GraphNode;
  readonly edges: ReadonlyArray<GraphEdge>;
}

/** Read every typed :CatalogImport prov:Activity node + its
 *  WAS_GENERATED_BY edges that we've persisted for this project. */
export function readImportActivities(projectId: string): { nodes: ReadonlyArray<GraphNode>; edges: ReadonlyArray<GraphEdge> } {
  try {
    const raw = window.localStorage.getItem(ACTIVITY_KEY_PREFIX + projectId);
    if (!raw) return { nodes: [], edges: [] };
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return { nodes: [], edges: [] };
    const acts = parsed.filter((a): a is PersistedActivity =>
      Boolean(a) && typeof a === "object"
      && Boolean((a as PersistedActivity).node)
      && (a as PersistedActivity).node.kind === "CatalogImport"
      && Array.isArray((a as PersistedActivity).edges),
    );
    return {
      nodes: acts.map((a) => a.node),
      edges: acts.flatMap((a) => a.edges),
    };
  } catch (err) {
    console.error("[catalogIngest] failed to read activities", err);
    return { nodes: [], edges: [] };
  }
}

/** Append one ingest run's typed activity + provenance edges. Dedupes by
 *  activity id so repeated re-seeds on the same project don't duplicate. */
export function appendImportActivity(projectId: string, node: GraphNode, edges: ReadonlyArray<GraphEdge>): void {
  try {
    const raw = window.localStorage.getItem(ACTIVITY_KEY_PREFIX + projectId);
    const existing: PersistedActivity[] = raw ? (JSON.parse(raw) as PersistedActivity[]) : [];
    const filtered = existing.filter((a) => a.node.id !== node.id);
    filtered.push({ node, edges });
    window.localStorage.setItem(ACTIVITY_KEY_PREFIX + projectId, JSON.stringify(filtered));
  } catch (err) {
    console.error("[catalogIngest] failed to persist activity", err);
  }
}

// ─── KV-backed API (cross-device) ──────────────────────────────────────

/** POST the materialized SKUs to /api/catalog/ingest so they persist in
 *  Cloudflare KV (cross-device). localStorage is the write-through cache
 *  so the UI never blocks on the network — but the canonical store is KV.
 *  Returns the server's IngestResult or null on transport failure. */
export async function pushToKvCatalog(
  projectId: string,
  skus: ReadonlyArray<GraphNode>,
): Promise<{ ok: boolean; appended: number; total: number } | null> {
  try {
    const res = await fetch("/api/catalog/ingest", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ projectId, skus }),
    });
    if (!res.ok) {
      // eslint-disable-next-line no-console
      console.error("[catalogIngest] /api/catalog/ingest failed", res.status, await res.text().catch(() => ""));
      return null;
    }
    const body = await res.json() as { ok: boolean; appended: number; total: number };
    return body;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[catalogIngest] /api/catalog/ingest threw", err);
    return null;
  }
}

/** GET /api/catalog/list?projectId=… — pull KV-persisted catalog. Falls
 *  back to localStorage when the network fails so designers keep working
 *  offline. */
export async function pullFromKvCatalog(projectId: string): Promise<ReadonlyArray<GraphNode>> {
  try {
    const res = await fetch(`/api/catalog/list?projectId=${encodeURIComponent(projectId)}`);
    if (!res.ok) return readImportedCatalog(projectId);
    const body = await res.json() as { ok: boolean; skus: ReadonlyArray<GraphNode> };
    if (!body.ok || !Array.isArray(body.skus)) return readImportedCatalog(projectId);
    // Refresh write-through cache so subsequent renders are instant.
    try { window.localStorage.setItem(KEY_PREFIX + projectId, JSON.stringify(body.skus)); } catch { /* cache best-effort */ }
    return body.skus;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[catalogIngest] /api/catalog/list threw — falling back to localStorage", err);
    return readImportedCatalog(projectId);
  }
}

// ─── Typed-array ingest (no CSV string) ────────────────────────────────

interface SeedEntry {
  readonly id: string;
  readonly label: string;
  readonly category: string;
  readonly price?: number;
  readonly leadDays?: number;
  readonly styleMatch?: number;
  readonly vendor?: string;
  readonly photoUrl?: string;
  readonly modelUrl?: string;
  readonly datasetId?: string;
  readonly rowIndex?: number;
  readonly ikeaArticle?: string;
}

/** Ingest a typed array of seed SKU entries directly (skips CSV parse).
 *  Use when the ontology declares pre-validated seed data — e.g.
 *  codegen.json#demoSeedSkus — so we don't round-trip through a CSV
 *  literal in components. CLAUDE.md §1 — ontology is the source of truth. */
export function ingestSeedEntries(args: {
  readonly source: CatalogSource;
  readonly entries: ReadonlyArray<SeedEntry>;
  readonly projectId: string;
  readonly nowIso?: string;
}): IngestResult {
  const now = args.nowIso ?? new Date().toISOString();
  const skus: GraphNode[] = [];
  const skipped: { rowIndex: number; reason: string }[] = [];

  for (let i = 0; i < args.entries.length; i += 1) {
    const e = args.entries[i] ?? null;
    if (!e) continue;
    const parsed = ImportedSkuSchema.safeParse({
      label: e.label,
      category: e.category,
      price: e.price,
      leadDays: e.leadDays,
      styleMatch: e.styleMatch,
      vendor: e.vendor,
      photoUrl: e.photoUrl,
      modelUrl: e.modelUrl,
    });
    if (!parsed.success) {
      skipped.push({ rowIndex: e.rowIndex ?? i + 1, reason: parsed.error.issues.map((iss) => `${iss.path.join(".")}: ${iss.message}`).join(" · ") });
      continue;
    }
    const sku = parsed.data;
    const attrs: Record<string, string | number | boolean | null> = {
      category: sku.category,
      _source: args.source,
      _datasetId: e.datasetId ?? "demo-seed",
      _rowIndex: e.rowIndex ?? i + 1,
      _importedAt: now,
    };
    if (sku.price !== undefined)       attrs["price"]       = sku.price;
    if (sku.leadDays !== undefined)    attrs["leadDays"]    = sku.leadDays;
    if (sku.styleMatch !== undefined)  attrs["styleMatch"]  = sku.styleMatch;
    if (sku.vendor !== undefined)      attrs["vendor"]      = sku.vendor;
    if (sku.photoUrl !== undefined)    attrs["photoUrl"]    = sku.photoUrl;
    if (sku.modelUrl !== undefined)    attrs["modelUrl"]    = sku.modelUrl;
    if (e.ikeaArticle)                 attrs["ikeaArticle"] = e.ikeaArticle;
    skus.push({ id: e.id, kind: "SKU", label: sku.label, attrs });
  }

  const dsId = args.entries[0]?.datasetId ?? "demo-seed";
  const { node: importActivity, edges: provenanceEdges } = buildImportActivity(
    args.source, dsId, args.projectId, now, skus.map((s) => s.id), skipped.length,
  );
  return {
    source: args.source,
    datasetId: dsId,
    importedAt: now,
    skus,
    skipped,
    importActivity,
    provenanceEdges,
  };
}
